/**
 * Note Slice
 * Redux Toolkit slice for note/communication log state management
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import type { Note, NoteType, CreateNoteRequest, UpdateNoteRequest } from '@creatoros/shared';
import { noteService } from '../../services/note.service';
import { asArray } from '../../services/api.unwrap';
import { ApiErrorState } from '../../types/error.types';
import { logger } from '../../utils/logger';

/**
 * Note State Interface
 */
export interface NoteState {
  notes: Note[];
  selectedNote: Note | null;
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: ApiErrorState | null;
  filters: {
    dealId?: string;
    noteType?: NoteType;
  };
}

/**
 * Initial State
 */
const initialState: NoteState = {
  notes: [],
  selectedNote: null,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
  filters: {},
};

/**
 * Async Thunks
 */

/**
 * Fetch notes by deal ID
 */
export const fetchNotesByDeal = createAsyncThunk<Note[], string, { rejectValue: ApiErrorState }>(
  'notes/fetchByDeal',
  async (dealId, { rejectWithValue }) => {
    try {
      logger.debug('Fetching notes', { event: 'notes.fetch_by_deal.started', dealId });
      const response = await noteService.getByDeal(dealId);
      const notes = asArray<Note>(response.data);
      logger.debug('Fetched notes', {
        event: 'notes.fetch_by_deal.succeeded',
        dealId,
        count: notes.length,
      });
      return notes;
    } catch (err: any) {
      logger.error('Failed to fetch notes', err, {
        event: 'notes.fetch_by_deal.failed',
        dealId,
        code: err.code || err.response?.data?.errorType,
      });
      return rejectWithValue({
        message: err.message || err.response?.data?.error || 'Failed to fetch notes',
        code: err.code || err.response?.data?.errorType,
        details: err.details || err.response?.data?.details,
        timestamp: new Date().toISOString(),
      });
    }
  }
);

/**
 * Fetch a single note by ID
 */
export const fetchNoteById = createAsyncThunk<Note, string, { rejectValue: ApiErrorState }>(
  'notes/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      logger.debug('Fetching note', { event: 'notes.fetch_by_id.started', noteId: id });
      const note = await noteService.getById(id);
      logger.debug('Fetched note', { event: 'notes.fetch_by_id.succeeded', noteId: id });
      return note;
    } catch (err: any) {
      logger.error('Failed to fetch note', err, {
        event: 'notes.fetch_by_id.failed',
        noteId: id,
        code: err.code || err.response?.data?.errorType,
      });
      return rejectWithValue({
        message: err.message || err.response?.data?.error || 'Failed to fetch note',
        code: err.code || err.response?.data?.errorType,
        details: err.details || err.response?.data?.details,
        timestamp: new Date().toISOString(),
      });
    }
  }
);

/**
 * Create a new note
 */
export const createNote = createAsyncThunk<Note, CreateNoteRequest, { rejectValue: ApiErrorState }>(
  'notes/create',
  async (data, { rejectWithValue }) => {
    try {
      const note = await noteService.create(data);
      logger.info('Note created', { event: 'notes.create.succeeded', noteId: note.id });
      return note;
    } catch (err: any) {
      logger.error('Failed to create note', err, {
        event: 'notes.create.failed',
        code: err.code || err.response?.data?.errorType,
      });
      return rejectWithValue({
        message: err.message || err.response?.data?.error || 'Failed to create note',
        code: err.code || err.response?.data?.errorType,
        details: err.details || err.response?.data?.details,
        timestamp: new Date().toISOString(),
      });
    }
  }
);

/**
 * Update an existing note
 */
export const updateNote = createAsyncThunk<
  Note,
  { id: string; data: UpdateNoteRequest },
  { rejectValue: ApiErrorState }
>('notes/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    const note = await noteService.update(id, data);
    logger.info('Note updated', { event: 'notes.update.succeeded', noteId: id });
    return note;
  } catch (err: any) {
    logger.error('Failed to update note', err, {
      event: 'notes.update.failed',
      noteId: id,
      code: err.code || err.response?.data?.errorType,
    });
    return rejectWithValue({
      message: err.message || err.response?.data?.error || 'Failed to update note',
      code: err.code || err.response?.data?.errorType,
      details: err.details || err.response?.data?.details,
      timestamp: new Date().toISOString(),
    });
  }
});

/**
 * Delete a note
 */
export const deleteNote = createAsyncThunk<string, string, { rejectValue: ApiErrorState }>(
  'notes/delete',
  async (id, { rejectWithValue }) => {
    try {
      await noteService.delete(id);
      logger.info('Note deleted', { event: 'notes.delete.succeeded', noteId: id });
      return id;
    } catch (err: any) {
      logger.error('Failed to delete note', err, {
        event: 'notes.delete.failed',
        noteId: id,
        code: err.code || err.response?.data?.errorType,
      });
      return rejectWithValue({
        message: err.message || err.response?.data?.error || 'Failed to delete note',
        code: err.code || err.response?.data?.errorType,
        details: err.details || err.response?.data?.details,
        timestamp: new Date().toISOString(),
      });
    }
  }
);

/**
 * Note Slice
 */
const noteSlice = createSlice({
  name: 'notes',
  initialState,
  reducers: {
    /**
     * Set selected note
     */
    setSelectedNote: (state, action: PayloadAction<Note | null>) => {
      state.selectedNote = action.payload;
    },

    /**
     * Clear selected note
     */
    clearSelectedNote: (state) => {
      state.selectedNote = null;
    },

    /**
     * Set filters
     */
    setNoteFilters: (
      state,
      action: PayloadAction<{
        dealId?: string;
        noteType?: NoteType;
      }>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    /**
     * Clear filters
     */
    clearNoteFilters: (state) => {
      state.filters = {};
    },

    /**
     * Clear error
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Reset state
     */
    resetNoteState: () => initialState,

    /**
     * Remove a single attachment from a note (optimistic UI update after delete)
     */
    removeNoteAttachment: (
      state,
      action: PayloadAction<{ noteId: string; attachmentId: string }>
    ) => {
      const note = state.notes.find((n) => n.id === action.payload.noteId);
      if (note && note.attachments) {
        note.attachments = note.attachments.filter((a) => a.id !== action.payload.attachmentId);
      }
      if (state.selectedNote?.id === action.payload.noteId && state.selectedNote.attachments) {
        state.selectedNote.attachments = state.selectedNote.attachments.filter(
          (a) => a.id !== action.payload.attachmentId
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch notes by deal
      .addCase(fetchNotesByDeal.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNotesByDeal.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notes = asArray(action.payload);
      })
      .addCase(fetchNotesByDeal.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? { message: 'Failed to fetch notes' };
      })

      // Fetch note by ID
      .addCase(fetchNoteById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNoteById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedNote = action.payload;
        // Update in list if exists
        const index = state.notes.findIndex((n) => n.id === action.payload.id);
        if (index !== -1) {
          state.notes[index] = action.payload;
        }
      })
      .addCase(fetchNoteById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? { message: 'Failed to fetch note' };
      })

      // Create note
      .addCase(createNote.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createNote.fulfilled, (state, action) => {
        state.isCreating = false;
        state.notes.push(action.payload);
      })
      .addCase(createNote.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload ?? { message: 'Failed to create note' };
      })

      // Update note
      .addCase(updateNote.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateNote.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.notes.findIndex((n) => n.id === action.payload.id);
        if (index !== -1) {
          state.notes[index] = action.payload;
        }
        if (state.selectedNote?.id === action.payload.id) {
          state.selectedNote = action.payload;
        }
      })
      .addCase(updateNote.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload ?? { message: 'Failed to update note' };
      })

      // Delete note
      .addCase(deleteNote.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteNote.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.notes = state.notes.filter((n) => n.id !== action.payload);
        if (state.selectedNote?.id === action.payload) {
          state.selectedNote = null;
        }
      })
      .addCase(deleteNote.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload ?? { message: 'Failed to delete note' };
      });
  },
});

/**
 * Selectors
 */

// Base selectors
export const selectNotes = (state: RootState) => asArray<Note>(state.notes.notes);
export const selectSelectedNote = (state: RootState) => state.notes.selectedNote;
export const selectNoteError = (state: RootState) => state.notes.error;
export const selectNoteIsLoading = (state: RootState) => state.notes.isLoading;
export const selectNoteIsCreating = (state: RootState) => state.notes.isCreating;
export const selectNoteIsUpdating = (state: RootState) => state.notes.isUpdating;
export const selectNoteIsDeleting = (state: RootState) => state.notes.isDeleting;
export const selectNoteFilters = (state: RootState) => state.notes.filters;

// Derived selectors
export const selectNoteById = (state: RootState, id: string) =>
  selectNotes(state).find((note) => note.id === id);

export const selectNotesByDeal = (state: RootState, dealId: string) =>
  selectNotes(state).filter((note) => note.dealId === dealId);

export const selectNotesByType = (state: RootState, noteType: NoteType) =>
  selectNotes(state).filter((note) => note.noteType === noteType);

export const selectEncryptedNotes = (state: RootState) =>
  selectNotes(state).filter((note) => note.isEncrypted);

export const selectRecentNotes = (state: RootState, limit = 10) =>
  [...selectNotes(state)]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, limit);

export const selectFilteredNotes = (state: RootState) => {
  let filtered = selectNotes(state);
  const { dealId, noteType } = state.notes.filters;

  if (dealId) {
    filtered = filtered.filter((note) => note.dealId === dealId);
  }
  if (noteType) {
    filtered = filtered.filter((note) => note.noteType === noteType);
  }

  return filtered;
};

export const selectNotesCount = (state: RootState) => selectNotes(state).length;

export const selectNotesByDeliverableId = (state: RootState, deliverableId: string) =>
  selectNotes(state).filter((note) => note.metadata?.relatedDeliverableId === deliverableId);

export const selectNotesBySentiment = (
  state: RootState,
  sentiment: 'positive' | 'neutral' | 'negative'
) => selectNotes(state).filter((note) => note.metadata?.sentiment === sentiment);

/**
 * Export actions and reducer
 */
export const {
  setSelectedNote,
  clearSelectedNote,
  setNoteFilters,
  clearNoteFilters,
  clearError,
  resetNoteState,
  removeNoteAttachment,
} = noteSlice.actions;

export default noteSlice.reducer;
